import { useNavigate } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { ShieldCheck } from 'lucide-react';
import { createContext, useCallback, useContext, useState } from 'react';
import { useAuth } from '~/auth/use-auth';
import { m } from '~/paraglide/messages.js';

interface KycGateContextValue {
  isVerified: boolean;
  openKycModal: () => void;
}

const KycGateContext = createContext<KycGateContextValue | null>(null);

export function useKycGate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const isVerified = user?.metadata?.kyc_verified || false;

  const openKycModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const goToVerification = useCallback(() => {
    setIsOpen(false);
    navigate({ to: '/verify-identity/kyc' });
  }, [navigate]);

  const KycModal = () => (
    <KycRequiredDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      onVerify={goToVerification}
    />
  );

  return { isVerified, openKycModal, KycModal };
}

interface KycGateProps {
  children: React.ReactNode;
}

export function KycGateProvider({ children }: KycGateProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const isVerified = user?.metadata?.kyc_verified || false;

  const openKycModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const goToVerification = useCallback(() => {
    setIsOpen(false);
    navigate({ to: '/verify-identity/kyc' });
  }, [navigate]);

  return (
    <KycGateContext.Provider value={{ isVerified, openKycModal }}>
      {children}
      <KycRequiredDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        onVerify={goToVerification}
      />
    </KycGateContext.Provider>
  );
}

export function useKycGateContext() {
  const context = useContext(KycGateContext);
  if (!context) {
    throw new Error('useKycGateContext must be used within a KycGateProvider');
  }
  return context;
}

interface KycRequiredDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerify: () => void;
}

function KycRequiredDialog({
  open,
  onOpenChange,
  onVerify,
}: KycRequiredDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="rounded-full bg-amber-500/10 p-4">
              <ShieldCheck className="h-8 w-8 text-amber-500" />
            </div>
            <DialogTitle className="text-center text-lg">
              {m['kyc_gate.modal_title']()}
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription className="text-center text-sm leading-relaxed">
          {m['kyc_gate.modal_description']()}
        </DialogDescription>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={onVerify} className="w-full">
            {m['kyc_gate.modal_verify_button']()}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-full text-muted-foreground"
          >
            {m['kyc_gate.modal_later_button']()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
